import {
    Alert,
    AlertTitle,
    Box,
    capitalize,
    Card,
    CardContent,
    Chip,
    DialogActions,
    FormControlLabel,
    FormHelperText,
    IconButton,
    Link,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {Field, Form, Formik, FormikProps, useFormikContext} from "formik";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {Aufgabe, AufgabenListe, PhoneNumber, PromptListe} from "../../api";
import {usePhoneNumber, useIcpFallbackPromptsByPhoneNumberId} from "../../client/queries";
import {usePhonenumbersHandling} from "../../hooks/phonenumbers/usePhonenumbersHandling";
import {usePhonenumberPromptHandling} from "../../hooks/phonenumbers/usePhonenumberPromptHandling";
import {usePhoneNumberUpdateForm} from "../../hooks/phonenumbers/usePhoneNumberUpdateForm";
import {PromptVersionEditorBase} from "../../components/aufgaben/AufgabePromptVersionEditor";
import SectionToolsPhoneNumber from "../../components/phonenumbers/SectionToolsPhoneNumber";
import FieldsInboundConfig from "../../components/shared/FieldsInboundConfig";
import GenericSelect from "../../components/data/GenericSelect";
import SubmitButton from "../../components/SubmitButton";
import ErrorAlert from "../../components/ErrorAlert";
import MultipleGuard from "../../components/guards/MultipleGuard";
import Layout from "../../components/Layout";
import {permissions} from "../../permissions";
import {notPermitted} from "../../errors";
import type {PhoneNumberFormValues} from "../../types/phonenumberTypes";
import {useSearchAufgaben, useGetAufgabe} from "../../client/hooks";
import {useCallback, useEffect, useState} from "react";

export default function PagePhonenumberVersions() {
    const {phoneNumberId = ""} = useParams<{phoneNumberId: string}>();
    const {t: translation} = useTranslation('phonenumbers');
    const navigate = useNavigate();
    const phoneNumber: PhoneNumber = usePhoneNumber(phoneNumberId);
    const versions: PromptListe = useIcpFallbackPromptsByPhoneNumberId(phoneNumberId);
    const {canEditPhoneNumber} = usePhonenumbersHandling();
    const {createPromptMutation, updatePromptMutation, setActivePromptMutation, deletePromptMutation} = usePhonenumberPromptHandling(phoneNumberId);
    const formikProps = usePhoneNumberUpdateForm(phoneNumber, versions);
    const canEdit = canEditPhoneNumber();
    const activeId = phoneNumber.activeIcpFallbackPromptId ?? undefined;
    const directInboundAufgaben = phoneNumber.usages.filter(u => u.type === 'AUFGABE' && u.use_inbound && !u.inherited);
    const overridingAufgaben = directInboundAufgaben.length === 1 ? directInboundAufgaben : [];

    return (
        <Layout title={phoneNumber.number} back="/phonenumbers">
        <MultipleGuard
            permissions={[permissions.phonenumbers.show, permissions.phonenumbers.edit]}
            fallback={<ErrorAlert error={notPermitted()}/>}
        >
            <Formik {...formikProps}>
                {({isSubmitting, isValidating, isValid, dirty, values, setFieldValue}: FormikProps<PhoneNumberFormValues>) => (
                    <Form id="form-update-phonenumber">
                        <Paper sx={{p: 2}}>
                        <Stack spacing={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconButton size="small" onClick={() => navigate('/phonenumbers')}>
                                    <ArrowBackIcon fontSize="small"/>
                                </IconButton>
                                <Typography variant="h6">{phoneNumber.number}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values.inboundEnabled}
                                            onChange={(e) => setFieldValue('inboundEnabled', e.target.checked)}
                                            color="primary"
                                            disabled={!canEdit}
                                        />
                                    }
                                    label={capitalize(translation('phonenumber-inbound-config-active'))}
                                />
                                {values.inboundEnabled
                                    ? <Chip color="success" size="small" label={capitalize(translation('phonenumber-inbound-config-enabled'))}/>
                                    : <Chip variant="outlined" size="small" label={capitalize(translation('phonenumber-inbound-config-disabled'))}/>}
                                {overridingAufgaben.length > 0 &&
                                    <Chip size="small" label={capitalize(translation('phonenumber-inbound-config-overridden'))}
                                        sx={{bgcolor: '#7c3aed', color: '#fff'}}/>}
                            </Box>
                            {overridingAufgaben.length > 0 && (
                                <Alert severity="warning">
                                    <AlertTitle>{translation('phonenumber-inbound-config-overridden-warning')}</AlertTitle>
                                    <ul style={{margin: 0, paddingLeft: 20}}>
                                        {overridingAufgaben.map(u => (
                                            <li key={u.id}>
                                                <Link component={RouterLink} to={`/aufgaben/${u.id}`} underline="hover">
                                                    {u.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                    {values.inboundEnabled && (
                                        <Typography variant="body2" sx={{mt: 1}}>
                                            {translation('phonenumber-inbound-config-fallback-hint')}
                                        </Typography>
                                    )}
                                </Alert>
                            )}
                            <PhoneNumberBaseFields canEdit={canEdit} inboundActive={values.inboundEnabled} phoneNumber={phoneNumber}/>
                            {values.inboundEnabled && (
                                <>
                            <SectionToolsPhoneNumber phoneNumber={phoneNumber} canEdit={canEdit}/>
                            <PromptVersionEditorBase
                                entityId={phoneNumber.id}
                                versions={versions}
                                activePromptId={activeId}
                                canEdit={canEdit}
                                title={translation('prompt-versions-title')}
                                noneSelectedText={translation('prompt-version-none-selected')}
                                createPromptMutation={createPromptMutation}
                                updatePromptMutation={updatePromptMutation}
                                setActivePromptMutation={setActivePromptMutation}
                                deletePromptMutation={deletePromptMutation}
                            />
                                </>
                            )}
                        </Stack>
                        </Paper>
                        <DialogActions
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                zIndex: 2,
                                px: 2,
                                py: 2,
                                backgroundColor: 'background.paper',
                                borderTop: 1,
                                borderColor: 'divider',
                            }}
                        >
                            <SubmitButton
                                id="page-update-phonenumber-submit-button"
                                form="form-update-phonenumber"
                                type="submit"
                                variant="contained"
                                color="secondary"
                                loading={isSubmitting}
                                disabled={isValidating || !isValid || !dirty || !canEdit}
                            >
                                {isSubmitting
                                    ? capitalize(translation('phonenumber-config-saving'))
                                    : capitalize(translation('phonenumber-config-save'))}
                            </SubmitButton>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </MultipleGuard>
        </Layout>
    );
}

function PhoneNumberBaseFields({canEdit, inboundActive, phoneNumber}: {canEdit: boolean; inboundActive: boolean; phoneNumber: PhoneNumber}) {
    const {t: translation} = useTranslation('phonenumbers');
    const {values, setFieldValue} = useFormikContext<PhoneNumberFormValues>();
    const searchAufgaben = useSearchAufgaben();
    const getAufgabe = useGetAufgabe();
    const [initialAufgabe, setInitialAufgabe] = useState<Aufgabe | undefined>(undefined);

    useEffect(() => {
        if (phoneNumber.inboundAufgabeId && !initialAufgabe) {
            getAufgabe(phoneNumber.inboundAufgabeId).then(setInitialAufgabe).catch(() => {});
        }
    }, [phoneNumber.inboundAufgabeId, getAufgabe, initialAufgabe]);

    const loadAufgaben = useCallback(async (params: any, filter: any): Promise<AufgabenListe> => {
        const search: string | undefined = filter.search?.toLowerCase();
        const page: number = params.page ?? 0;
        const limit: number = params.limit ?? 20;
        const sort: string | undefined = params.sort ?? undefined;
        return await searchAufgaben({search, page, limit, sort});
    }, [searchAufgaben]);

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="subtitle1" sx={{mb: 2}}>
                    {capitalize(translation('phonenumber-config-title'))}
                </Typography>
                <Stack spacing={2}>
                    <TextField
                        label={capitalize(translation('phonenumber-descriptor'))}
                        name="descriptor"
                        value={values.descriptor}
                        onChange={(e) => setFieldValue('descriptor', e.target.value)}
                        disabled={!canEdit}
                        size="small"
                    />
                    <div>
                        <FormControlLabel
                            control={<Switch checked={values.isGlobal} onChange={(e) => setFieldValue('isGlobal', e.target.checked)} color="primary" disabled={!canEdit}/>}
                            label={capitalize(translation('phonenumber-fallback'))}
                        />
                        <FormHelperText>{translation('phonenumber-fallback-help')}</FormHelperText>
                    </div>
                    {inboundActive && (
                        <>
                            <Field
                                component={GenericSelect}
                                name="inboundAufgabeId"
                                label={capitalize(translation('phonenumber-inbound-aufgabe'))}
                                loadOptions={loadAufgaben}
                                getOptionLabel={(aufgabe: Aufgabe) => aufgabe.titel}
                                getSecondaryLabel={(aufgabe: Aufgabe) => aufgabe.id}
                                setSelected={(value: string) => setFieldValue("inboundAufgabeId", value)}
                                initialSelected={initialAufgabe}
                                required={false}
                                sort={"titel"}
                                disabled={!canEdit}
                            />
                            <FieldsInboundConfig canEdit={canEdit}/>
                        </>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
